/**
 * R5 Mindest-Ist-Besteuerung (§ 13 Abs. 1 Nr. 1a S. 4 UStG): Wird das Entgelt vor der Leistung
 * vereinnahmt, entsteht die Steuer im VAZ der Vereinnahmung. Vergleich auf VAZ-Ebene: Zahlung in
 * einem früheren VAZ → MINDEST_IST dort mit min(Zahlung, offener Soll), im Leistungs-VAZ die
 * Gegenzeile MINDEST_IST_ABZUG (negativ). Gleicher VAZ → keine Zeilen (T03/T04). Zahlungen werden je
 * früherem VAZ netto saldiert (Rücklastschrift im selben VAZ hebt die Anzahlung auf); Zahlungen, die R6
 * ausschließt (Kaution, Mahngebühr, Adjustment EXCLUDE/DURCHLAUFEND), zählen nicht als Anzahlung.
 * R10-Kopplung: Zufluss in einem Jahr ohne Regelbesteuerung → kein Paar, BLOCKER PROFILE_YEAR_MISMATCH.
 *
 * @author Anthony Nahas
 * @since 11.09.26
 */
import {createIssue} from '../issues';
import {buildLine, buildSource, setupDeepLink} from '../line';
import {monthKey, monthLabel, monthStart, resolveLeistungsmonat, vazIndex} from '../month';
import {round2, signedAmount} from '../rounding';
import {BookingLite, EMPTY_RULE_OUTPUT, RuleContext, RuleOutput} from '../types';
import {classifyBooking, classifyPayment, issueIDsOf, linkIssues} from './exclusions';
import {classifyBookingForSoll} from './profile-wechsel';
import {artLabel, sollGross} from './soll';

export const buildMindestIstLines = (ctx: RuleContext, bookings: BookingLite[]): RuleOutput => {
    const out = EMPTY_RULE_OUTPUT();

    for (const booking of bookings) {
        const c = classifyBooking(ctx, booking);
        if (c.excluded) {
            continue;
        }
        const lm = resolveLeistungsmonat(booking);
        if (!lm || classifyBookingForSoll(ctx.profileAt, monthStart(lm.monthKey)) !== 'SOLL') {
            continue;
        }

        const leistungVaz = vazIndex(lm.monthKey, ctx.range.periodType);
        let openSoll = sollGross(booking, lm.monthKey);
        if (openSoll <= 0) {
            continue;
        }

        // Kandidaten: Zahlungen in einem früheren VAZ, die R6 nicht ausschließt (Kaution, Mahngebühr, Adjustment EXCLUDE …).
        // Je VAZ wird netto saldiert (Anzahlung + Rücklastschrift im selben VAZ = nie vereinnahmt), nur ein positives
        // VAZ-Netto zählt als Anzahlung; die Zuordnung auf einzelne Zahlungen läuft in Datumsreihenfolge.
        const candidates = (ctx.paymentsByBooking.get(booking.id) ?? []).filter(p =>
            p.date !== null && !classifyPayment(ctx, p).excluded && vazIndex(monthKey(p.date), ctx.range.periodType) < leistungVaz);
        const netByVaz = new Map<number, number>();
        for (const p of candidates) {
            const vaz = vazIndex(monthKey(p.date), ctx.range.periodType);
            netByVaz.set(vaz, round2((netByVaz.get(vaz) ?? 0) + signedAmount(p.amount, p.type)));
        }

        const lineIDs: string[] = [];
        let currentVaz: number | null = null;
        let vazCap = 0;
        for (const p of candidates) {
            const amount = signedAmount(p.amount, p.type);
            if (amount <= 0) {
                continue;
            }
            const paymentKey = monthKey(p.date);
            const vaz = vazIndex(paymentKey, ctx.range.periodType);
            if (vaz !== currentVaz) {
                currentVaz = vaz;
                vazCap = round2(Math.min(Math.max(0, netByVaz.get(vaz) ?? 0), openSoll));
            }
            if (vazCap <= 0) {
                continue;
            }
            // R10: Anzahlung aus einem Jahr ohne Regelbesteuerung (Kleinunternehmer/steuerfrei) auf eine Leistung
            // im Regel-SOLL-Jahr bleibt vollständig im Leistungszeitraum steuerpflichtig (Abschn. 19.5 UStAE) –
            // kein Mindest-Ist-Paar, sonst Nichtbesteuerung. Nur Status prüfen: IST→SOLL läuft regulär über R5.
            if (ctx.profileAt(p.date).status !== 'REGELBESTEUERUNG') {
                out.issues.push(createIssue('PROFILE_YEAR_MISMATCH', p.id,
                    `Die Anzahlung vom ${monthLabel(paymentKey)} auf ${artLabel(booking.art)} ${monthLabel(lm.monthKey)} floss in einem Jahr ohne Regelbesteuerung zu. Sie bleibt vollständig im Leistungszeitraum steuerpflichtig (Abschn. 19.5 UStAE); bitte prüfen Sie das Steuerprofil.`,
                    {deepLink: setupDeepLink(ctx.landlordID)}));
                continue;
            }
            const anzahlung = round2(Math.min(amount, vazCap));
            vazCap = round2(vazCap - anzahlung);
            openSoll = round2(openSoll - anzahlung);

            const source = buildSource('PAYMENT', {id: p.id, propertyID: p.propertyID, unitID: p.unitID ?? booking.unitID, periodID: booking.periodID});
            const issueIDs = [...issueIDsOf(c.issues), `MINDEST_IST_INFO_${booking.id}`];
            const mindest = buildLine({
                rule: 'MINDEST_IST', source, monthKey: paymentKey, monthSource: 'PAYMENT_DATE',
                category: c.category, rate: c.rate, gross: anzahlung,
                label: `Anzahlung auf ${artLabel(booking.art)} ${monthLabel(lm.monthKey)} (Zufluss ${monthLabel(paymentKey)})`, issueIDs,
            });
            const abzug = buildLine({
                rule: 'MINDEST_IST_ABZUG', source, monthKey: lm.monthKey, monthSource: lm.monthSource,
                category: c.category, rate: c.rate, gross: -anzahlung,
                label: `Abzug Anzahlung ${monthLabel(paymentKey)} für ${artLabel(booking.art)} ${monthLabel(lm.monthKey)}`, issueIDs,
            });
            out.lines.push(mindest, abzug);
            lineIDs.push(mindest.id, abzug.id);
            out.issues.push(...linkIssues(linkIssues(c.issues, mindest.id), abzug.id));
        }

        if (lineIDs.length > 0) {
            out.issues.push(createIssue('MINDEST_IST_INFO', booking.id,
                `Für ${artLabel(booking.art)} ${monthLabel(lm.monthKey)} wurden Anzahlungen vor dem Leistungszeitraum vereinnahmt. Die Steuer entsteht im Zuflusszeitraum (§ 13 Abs. 1 Nr. 1a S. 4 UStG); im Leistungszeitraum wird der Betrag wieder abgezogen.`,
                {lineIDs}));
        }
    }

    return out;
};
